import { motion } from "framer-motion";
import { useImageOverrides, resolveImage } from "../hooks/useImageOverrides";
import { useQuestionOverrides, resolveTitle, resolveHelper, resolveLabel } from "../hooks/useQuestionOverrides";

export default function QuestionCard({ q, lang, selected, onSelect }) {
  const overrides = useImageOverrides();
  const textOverrides = useQuestionOverrides();
  const helperText = q.helper ? resolveHelper(textOverrides, q.id, lang, q.helper) : "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="w-full"
      data-testid={`question-${q.id}`}
    >
      <h2 className="text-3xl sm:text-4xl leading-tight text-[#383633] tracking-tight mb-3 font-medium">
        {resolveTitle(textOverrides, q.id, lang, q.title)}
      </h2>
      {helperText && (
        <p className="text-sm italic text-[#6F6B66] mb-6 max-w-lg" data-testid={`helper-${q.id}`}>
          {helperText}
        </p>
      )}
      <motion.div
        variants={{ show: { transition: { staggerChildren: 0.06 } } }}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 gap-3"
      >
        {q.options.map((opt) => {
          const isSel = selected === opt.id;
          // Allow an option to carry its own admin-override lookup key
          // (used by the age question to keep women/men image overrides
          // strictly independent — see /src/config/landing.js
          // ageQuestionForGender). Falls back to the standard q_id/opt_id
          // scheme for every other question.
          const overrideKey = opt.overrideKey || `q_${q.id}_${opt.id}`;
          const imgSrc = resolveImage(overrides, overrideKey, opt.image);
          const label = resolveLabel(textOverrides, q.id, opt.id, lang, opt.label);
          return (
            <motion.button
              key={opt.id}
              variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
              onClick={() => onSelect(opt.id)}
              data-testid={`option-${q.id}-${opt.id}`}
              className={`answer-card group relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white kt-shadow text-left ${isSel ? "selected" : ""}`}
            >
              <div className="h-32 w-full overflow-hidden bg-[#F3F3F3]">
                <img src={imgSrc} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
              </div>
              <div className="p-3 flex items-center justify-between">
                <span className="text-sm text-[#383633] leading-snug">{label}</span>
                <span className={`h-4 w-4 rounded-full border ${isSel ? "bg-[#383633] border-[#383633]" : "border-[#383633]"}`} />
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </motion.div>
  );
}
